import { useAtomValue } from "@effect/atom-react";
import { isAssetAttachmentNotFoundFailure } from "@afar-research/client-runtime/state/attachments";
import { resolveAssetUrl } from "@afar-research/client-runtime/state/assets";
import type { AssetResource, EnvironmentId } from "@afar-research/contracts";
import { AsyncResult } from "effect/unstable/reactivity";
import { useCallback, useMemo } from "react";

import { squashAtomCommandFailure } from "@afar-research/client-runtime/state/runtime";

import { assetEnvironment } from "~/state/assets";
import { useAtomQueryRunner } from "~/state/use-atom-query-runner";
import { usePreparedConnection } from "~/state/session";

export { resolveAssetUrl } from "@afar-research/client-runtime/state/assets";

export function useAssetUrl(environmentId: EnvironmentId, resource: AssetResource): string | null {
  const preparedConnection = usePreparedConnection(environmentId);
  const result = useAtomValue(
    assetEnvironment.createUrl({
      environmentId,
      input: { resource },
    }),
  );
  if (preparedConnection._tag === "None" || result._tag !== "Success") {
    return null;
  }
  return resolveAssetUrl(preparedConnection.value.httpBaseUrl, result.value.relativeUrl);
}

/**
 * What the App knows about one asset right now.
 *
 * "Unavailable" is a distinct answer from "not yet": an attachment that
 * references a file the user has since moved or deleted can never resolve, and
 * the difference is what lets the UI say so instead of showing a spinner
 * forever or a chip that silently does nothing.
 */
export type AssetUrlState =
  | { readonly status: "pending" }
  | { readonly status: "unavailable" }
  | { readonly status: "failed" }
  | { readonly status: "ready"; readonly url: string };

export function assetUrlState(
  result: AsyncResult.AsyncResult<{ readonly relativeUrl: string }, unknown>,
  httpBaseUrl: string,
): AssetUrlState {
  if (AsyncResult.isSuccess(result)) {
    const url = resolveAssetUrl(httpBaseUrl, result.value.relativeUrl);
    // An invalid URL says nothing about whether the host path still exists.
    return url === null ? { status: "failed" } : { status: "ready", url };
  }
  if (AsyncResult.isWaiting(result) || AsyncResult.isInitial(result)) {
    return { status: "pending" };
  }
  // A failed lookup is not evidence that the user's path disappeared: signing
  // and transport can fail after the Environment has verified the attachment.
  return isAssetAttachmentNotFoundFailure(squashAtomCommandFailure(result))
    ? { status: "unavailable" }
    : { status: "failed" };
}

export function useAssetUrlStates(
  environmentId: EnvironmentId,
  resources: ReadonlyArray<AssetResource>,
): ReadonlyArray<AssetUrlState> {
  const preparedConnection = usePreparedConnection(environmentId);
  const results = useAtomValue(
    assetEnvironment.createUrls({
      environmentId,
      resources,
    }),
  );
  return useMemo(
    () =>
      preparedConnection._tag === "None"
        ? resources.map(() => ({ status: "pending" }) as const)
        : results.map((result) => assetUrlState(result, preparedConnection.value.httpBaseUrl)),
    [preparedConnection, resources, results],
  );
}

export function useAssetUrls(
  environmentId: EnvironmentId,
  resources: ReadonlyArray<AssetResource>,
): ReadonlyArray<string | null> {
  const states = useAssetUrlStates(environmentId, resources);
  return useMemo(
    () => states.map((state) => (state.status === "ready" ? state.url : null)),
    [states],
  );
}

/**
 * Mints a fresh URL for one asset on demand. Asset URLs expire, so a surface
 * that keeps one across a long-lived view asks for a new one rather than
 * holding a stale link.
 */
export function useAssetUrlRefresh(
  environmentId: EnvironmentId | null,
  resource: AssetResource | null,
): () => Promise<string | null> {
  const connection = usePreparedConnection(environmentId);
  const httpBaseUrl = connection._tag === "Some" ? connection.value.httpBaseUrl : null;
  const refresh = useAtomQueryRunner(assetEnvironment.createUrl, { reportFailure: false });
  return useCallback(async () => {
    if (environmentId === null || resource === null || httpBaseUrl === null) return null;
    const result = await refresh({ environmentId, input: { resource } });
    if (result._tag === "Failure") throw squashAtomCommandFailure(result);
    return resolveAssetUrl(httpBaseUrl, result.value.relativeUrl);
  }, [environmentId, resource, refresh, httpBaseUrl]);
}
